import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../services/api'
import { useOrganization } from '../contexts/OrganizationContext'

interface OrgSettings {
  features?: {
    ambulance_orders_enabled?: boolean
  }
}

interface UserInfo {
  role: string
  is_system_admin_org: boolean
  can_review_invoices?: boolean
  can_view_reports?: boolean
  can_order_ambulance?: boolean
  can_review_ambulance?: boolean
  organization_settings?: OrgSettings
}

export function usePermissions() {
  const { selectedOrgId } = useOrganization()

  const { data: userInfo, isLoading: userLoading } = useQuery<UserInfo>({
    queryKey: ['currentUser'],
    queryFn: () => apiClient.get('/auth/me'),
    staleTime: 5 * 60 * 1000,
  })

  const isAdmin = userInfo?.role === 'system_admin' || userInfo?.role === 'organization_admin'
  const isSystemAdmin = userInfo?.role === 'system_admin' ||
    (userInfo?.role === 'organization_admin' && userInfo?.is_system_admin_org)

  // Sys admins act on behalf of the organization selected in the top bar, so
  // feature gating must follow the selected org — not the sys admin's own org.
  // Shares the query cache with Layout (same key), so no extra request.
  const { data: selectedOrgData, isError: selectedOrgError } = useQuery<{ settings?: OrgSettings }>({
    queryKey: ['organization', selectedOrgId],
    queryFn: () => apiClient.get(`/organizations/${selectedOrgId}`),
    enabled: !!isSystemAdmin && !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
  })

  // Wait for the selected org's settings before deciding ambulance access.
  // Otherwise the route guard would briefly fall back to the own-org flag and
  // redirect to /dashboard before the selected org loads.
  const waitingForSelectedOrg =
    !!isSystemAdmin && !!selectedOrgId && selectedOrgData === undefined && !selectedOrgError
  const isLoading = userLoading || waitingForSelectedOrg

  // Backend already sets can_review_invoices/can_view_reports=true for admins server-side
  const canAccessInvoices = userInfo?.can_review_invoices
  const canAccessReports = userInfo?.can_view_reports

  // Effective org settings: selected org for sys admins (when one is chosen),
  // otherwise the user's own org. On selected-org fetch error, fall back to own org.
  const effectiveOrgSettings = (isSystemAdmin && selectedOrgId && selectedOrgData)
    ? selectedOrgData.settings
    : userInfo?.organization_settings
  const ambulanceEnabled = effectiveOrgSettings?.features?.ambulance_orders_enabled === true
  const canAccessAmbulance = ambulanceEnabled && (
    isSystemAdmin || userInfo?.can_order_ambulance || userInfo?.can_review_ambulance
  )
  const canReviewAmbulance = ambulanceEnabled && (
    isSystemAdmin || userInfo?.can_review_ambulance
  )

  return {
    isLoading,
    isAdmin: !!isAdmin,
    isSystemAdmin: !!isSystemAdmin,
    canAccessInvoices: !!canAccessInvoices,
    canAccessReports: !!canAccessReports,
    canAccessAmbulance: !!canAccessAmbulance,
    canReviewAmbulance: !!canReviewAmbulance,
  }
}
